import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { DataTypeProvider } from "@devexpress/dx-react-grid";
import {
  Grid,
  Table,
  TableHeaderRow,
} from "@devexpress/dx-react-grid-bootstrap4";

import { useSelectedMediaPlanModal } from "../create-media-plan-modal/use-selected-media-plan-modal";
import { useIsAutoblessedModelsEnabled } from "../hooks/use-is-autoblessed-models-enabled";

import { CountTokenTypeProvider, StatusTypeProvider } from "./cell-components";
import { DeleteCycleFormatter } from "./cell-components/delete-cycle-cell-component";
import { TABLE_COLUMN_EXTENSIONS, TABLE_COLUMNS } from "./consts";

// To support the FF'd changing of types of MediaPlan
// and to only use the necessary properties used in the
// MediaPlanTable
export type MediaPlanTableInterface = {
  id: string;
  name: string;
  date_range: {
    start_date: string;
    end_date: string;
  };
  spend_model_data_columns: {
    id: string;
  }[];
  kpis: {
    id: string;
  }[];
};

const TableComponent = ({ ...restProps }) => (
  <Table.Table
    {...restProps}
    className="[&_tbody>tr:nth-child(odd)>*]:bg-v2-secondary font-sans text-sm"
  />
);

const TableHeaderCell = ({ ...restProps }: TableHeaderRow.CellProps) => (
  <TableHeaderRow.Cell
    {...restProps}
    className="bg-v2-primary text-xs font-bold py-2"
  />
);
const TableCell = ({
  onSelect,
  ...restProps
}: Table.DataCellProps & { onSelect?: (id: string) => void }) => {
  const { openCreateMediaPlanModal, setSelectedMediaPlanId } =
    useSelectedMediaPlanModal();

  const areAutoblessedModelsEnabled = useIsAutoblessedModelsEnabled();

  let onClick = undefined;
  if (restProps.column.name === "name") {
    onClick = () => {
      if (areAutoblessedModelsEnabled) {
        onSelect?.(restProps.row.id);
      } else {
        setSelectedMediaPlanId(restProps.row.id);
        openCreateMediaPlanModal();
      }
    };
  }

  return (
    <Table.Cell
      {...restProps}
      onClick={onClick}
      className={twMerge(
        clsx("border-l-0", {
          "text-cosmic cursor-pointer": restProps.column.name === "name",
          "border-l-1": restProps.column.name === "date_range",
        }),
      )}
    />
  );
};

export const MediaPlanTable = ({
  mediaPlanList,
  onSelect,
}: {
  mediaPlanList: MediaPlanTableInterface[];
  onSelect?: (id: string) => void;
}) => {
  return (
    <div className="card [&_.dx-g-bs4-table-container]:rounded">
      <Grid rows={mediaPlanList} columns={TABLE_COLUMNS}>
        <CountTokenTypeProvider for={["channels", "kpis"]} />
        <StatusTypeProvider for={["status"]} />
        <DataTypeProvider
          for={["delete"]}
          formatterComponent={DeleteCycleFormatter}
        />
        <Table
          tableComponent={TableComponent}
          cellComponent={({ ...props }) => (
            <TableCell {...props} onSelect={onSelect} />
          )}
          columnExtensions={TABLE_COLUMN_EXTENSIONS}
        />
        <TableHeaderRow cellComponent={TableHeaderCell} />
      </Grid>
    </div>
  );
};
